import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import portalApi from "../../api/portalClient";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { useBranding } from "../../context/BrandingContext";
import { checkPassword } from "../../utils/passwordPolicy";

/** Invite / set-password landing page. The customer arrives here from the
 * emailed link (?token=…), chooses a password, and is signed straight into the
 * portal. The token is single-use + time-limited (validated server-side). */
export default function PortalSetPassword() {
  const { acceptSsoToken } = usePortalAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      setValid(false);
      return;
    }
    portalApi
      .get(`/api/v1/portal/set-password/validate?token=${encodeURIComponent(token)}`)
      .then((r) => {
        setValid(!!r.data.valid);
        setEmail(r.data.email || "");
        setCustomerName(r.data.customer_name || "");
      })
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const problem = checkPassword(password);
    if (problem) return setError(problem);
    if (password !== confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      const r = await portalApi.post("/api/v1/portal/set-password", { token, password });
      await acceptSsoToken(r.data.access_token);
      navigate("/portal");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Couldn't set your password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
        <div className="h-1.5 w-full bg-brand" />
        <div className="p-8">
          <div className="text-center mb-6">
            <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-semibold mb-1">
              {branding.portal_eyebrow}
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {valid ? "Set your password" : "Portal invite"}
            </h1>
            {valid && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {customerName ? `${customerName} portal` : "Customer portal"}
                {email ? ` — ${email}` : ""}
              </p>
            )}
          </div>

          {checking ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">Checking your link…</p>
          ) : !valid ? (
            <div className="text-center">
              <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                This link is invalid, expired, or already used. Ask your provider to send a new
                invite.
              </p>
              <button
                onClick={() => navigate("/portal/login")}
                className="mt-5 w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark"
              >
                Go to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  autoComplete="new-password"
                  className="w-full border border-gray-300 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Confirm password
                </label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full border border-gray-300 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Setting…" : "Set password & sign in"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
